
import React, { ReactElement } from "react";
import { gql, useMutation } from "@apollo/client";
import { CreateWalletKycMutationMutation, CreateWalletKycMutationMutationVariables } from "../../../../schemaTypes";

export const createWalletKycMutation = gql`
  mutation CreateWalletKycMutation(
    $nin: String!
    $dob: String!
    $bvn: String!
  ) {
    createWalletKyc(
      nin: $nin,
      dob: $dob,
      bvn: $bvn
    ) {
      errors {
        path
        message
      }
      message
    }
  }
`;

export interface WithCreateWalletKyc {
  createWalletKyc: (variables: CreateWalletKycMutationMutationVariables) => Promise<{
    errors: Array<{ path: string; message: string }> | null;
    message?: string | null;
    supportMeta?: {
      code?: string;
      supportRef?: string;
    };
  }>;
}

interface Props {
  children: (data: WithCreateWalletKyc) => ReactElement | null;
}

export const CreateWalletKyc: React.FC<Props> = ({ children }) => {
  const [mutate] = useMutation<
    CreateWalletKycMutationMutation,
    CreateWalletKycMutationMutationVariables
  >(createWalletKycMutation);

  const createWalletKyc = async (variables: CreateWalletKycMutationMutationVariables) => {
    try {
      const response = await mutate({ variables });
      const result = response?.data?.createWalletKyc;

      if (!result) {
        return {
          errors: [{ path: "general", message: "No response from server." }],
          message: undefined,
          supportMeta: undefined,
        };
      }

      const normalizedErrors = (result.errors || [])
        .filter((err): err is NonNullable<typeof err> => Boolean(err))
        .map((err) => ({
          path: err.path || "general",
          message: err.message,
        }));

      return {
        errors: normalizedErrors.length > 0 ? normalizedErrors : null,
        message: result.message,
        supportMeta: undefined,
      };
    } catch (err) {
      const maybeApolloErr = err as {
        message?: string;
        graphQLErrors?: Array<{ message?: string; extensions?: { code?: string; supportRef?: string } }>;
      };

      const firstGraphQLError = maybeApolloErr.graphQLErrors?.[0];
      const message =
        firstGraphQLError?.message ||
        maybeApolloErr.message ||
        "Failed to access wallet. Please try again.";

      return {
        errors: [{ path: "general", message }],
        message: undefined,
        supportMeta: {
          ...(typeof firstGraphQLError?.extensions?.code === "string"
            ? { code: firstGraphQLError.extensions.code }
            : {}),
          ...(typeof firstGraphQLError?.extensions?.supportRef === "string"
            ? { supportRef: firstGraphQLError.extensions.supportRef }
            : {}),
        },
      };
    }
  };

  return children({ createWalletKyc });
};